import { useState } from 'react';
import { CurrencyFlag } from '@/components/ui/CurrencyFlag';
import { useOnboarding } from '../OnboardingContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, 
  CreditCard, 
  Globe, 
  ArrowLeftRight, 
  Smartphone,
  Check,
  AlertCircle,
  Coins,
  Network,
  Sparkles
} from 'lucide-react';
import shopifyLogo from '@/assets/shopify-logo.png';
import woocommerceLogo from '@/assets/woocommerce-logo.png';
import magentoLogo from '@/assets/magento-logo.png';
import pinnaclecartLogo from '@/assets/pinnaclecart-logo.png';

const SERVICES = [
  { 
    id: 'business_account', 
    label: 'Business Account', 
    description: 'Multi-currency business banking',
    icon: Building2 
  },
  { 
    id: 'pos_terminal', 
    label: 'POS Terminal', 
    description: 'In-store card payment terminals',
    icon: Smartphone 
  },
  { 
    id: 'online_card_processing', 
    label: 'Online Card Processing', 
    description: 'Accept payments on your website',
    icon: CreditCard 
  },
  { 
    id: 'corporate_fx', 
    label: 'Corporate FX', 
    description: 'Foreign exchange services',
    icon: ArrowLeftRight 
  },
  { 
    id: 'international_payments', 
    label: 'International Payments', 
    description: 'Send and receive global payments',
    icon: Globe 
  },
  {
    id: 'baas',
    label: 'BaaS (Banking as a Service)',
    description: 'Open SEPA Accounts and receive/make payments for your clients',
    icon: Network,
    isBaaS: true,
  },
];

// Available currencies (EUR is mandatory for Business Account)
const CURRENCIES = [
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', flag: '🇨🇭' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł', flag: '🇵🇱' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', flag: '🇭🇺' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', flag: '🇷🇴' },
  
];

export function CorporateServicesStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const company = data.corporateCompany;
  
  // Initialize from saved data
  const [selectedServices, setSelectedServices] = useState<string[]>(
    data.corporateCompany?.servicesRequested || []
  );
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(
    data.corporateCompany?.currencyAccounts || ['EUR']
  );

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">Company data not found.</p>
        </div>
      </div>
    );
  }

  // These three are always required when any service is selected
  const CORE_SERVICES = ['business_account', 'corporate_fx', 'international_payments'];

  const toggleService = (serviceId: string) => {
    // BaaS is a standalone toggle — it does not interact with core services
    if (serviceId === 'baas') {
      setSelectedServices(prev =>
        prev.includes('baas') ? prev.filter(s => s !== 'baas') : [...prev, 'baas']
      );
      return;
    }

    setSelectedServices(prev => {
      const isCore = CORE_SERVICES.includes(serviceId);

      if (prev.includes(serviceId) && !isCore) {
        const remaining = prev.filter(s => s !== serviceId);
        const nonCoreRemaining = remaining.filter(s => !CORE_SERVICES.includes(s) && s !== 'baas');
        if (nonCoreRemaining.length === 0) {
          setSelectedCurrencies(['EUR']);
          return remaining.filter(s => s === 'baas');
        }
        return remaining;
      }

      if (prev.includes(serviceId) && isCore) {
        setSelectedCurrencies(['EUR']);
        return prev.filter(s => s === 'baas');
      }

      const withCore = Array.from(new Set([...prev, ...CORE_SERVICES, serviceId]));
      return withCore;
    });
  };

  const toggleCurrency = (currencyCode: string) => {
    if (currencyCode === 'EUR') return;
    
    setSelectedCurrencies(prev => {
      if (prev.includes(currencyCode)) {
        return prev.filter(c => c !== currencyCode);
      }
      if (prev.length >= 3) return prev;
      return [...prev, currencyCode];
    });
  };

  const hasBusinessAccount = selectedServices.includes('business_account');
  const additionalCurrencies = selectedCurrencies.filter(c => c !== 'EUR');
  const isValid = selectedServices.length > 0;

  const handleContinue = () => {
    if (!isValid) return;
    updateData({
      corporateCompany: {
        ...company,
        servicesRequested: selectedServices,
        currencyAccounts: hasBusinessAccount ? selectedCurrencies : undefined,
      }
    });
    goToNextStep();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-lg mx-auto w-full">
        <div className="animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary/10">
              <Coins className="w-8 h-8 text-primary" />
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                <Sparkles className="w-3 h-3 text-accent" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
              Select Your Services
            </h1>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">
              Build your ideal banking package — choose the services your business needs
            </p>
          </div>

          {/* Services Selection */}
          <div className="space-y-3 mb-8">
            {SERVICES.map((service) => {
              const Icon = service.icon;
              const isSelected = selectedServices.includes(service.id);
              const isCore = CORE_SERVICES.includes(service.id);
              const isAutoSelected = isCore && selectedServices.length > 0 && isSelected;
              const isBaaS = (service as any).isBaaS;

              // BaaS tile
              if (isBaaS) {
                return (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service.id)}
                    className={`group w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-start gap-4 text-left ${
                      isSelected
                        ? 'border-accent bg-accent/10 shadow-md shadow-accent/10'
                        : 'border-border bg-card hover:border-accent/40 hover:shadow-sm'
                    }`}
                  >
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? 'bg-accent text-accent-foreground' : 'bg-muted group-hover:bg-muted/80'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-sm">{service.label}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-accent bg-accent/10 px-1.5 py-0.5 rounded-full">
                          New
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground leading-relaxed">{service.description}</span>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-accent-foreground" />
                      </div>
                    )}
                  </button>
                );
              }

              // Online Card Processing — special layout with logos
              if (service.id === 'online_card_processing') {
                return (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service.id)}
                    className={`group w-full rounded-2xl border-2 transition-all duration-200 text-left overflow-hidden ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                        : 'border-border bg-card hover:border-muted-foreground/30 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-center gap-4 p-4 pb-2">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted group-hover:bg-muted/80'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm block">{service.label}</span>
                        <span className="text-xs text-muted-foreground">{service.description}</span>
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-1.5 shrink-0">
                          {isAutoSelected && (
                            <span className="text-[10px] font-medium text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded-full">
                              Included
                            </span>
                          )}
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-primary-foreground" />
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Integration logos */}
                    <div className="flex items-center justify-center gap-8 px-4 pb-4 pt-1">
                      <div className="flex flex-col items-center gap-1.5">
                        <img src={shopifyLogo} alt="Shopify" className="h-20 w-20 object-contain drop-shadow-md brightness-[1.8]" loading="lazy" />
                        <span className="text-xs font-semibold" style={{ color: '#95BF47' }}>Shopify</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5">
                        <img src={woocommerceLogo} alt="WooCommerce" className="h-20 w-20 object-contain drop-shadow-md brightness-[1.8]" loading="lazy" />
                        <span className="text-xs font-semibold" style={{ color: '#9B5C8F' }}>WooCommerce</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5">
                        <img src={magentoLogo} alt="Magento" className="h-20 w-20 object-contain drop-shadow-md brightness-[1.8]" loading="lazy" />
                        <span className="text-xs font-semibold" style={{ color: '#F26322' }}>Magento</span>
                      </div>
                      <div className="flex flex-col items-center gap-1.5">
                        <img src={pinnaclecartLogo} alt="PinnacleCart" className="h-20 w-20 object-contain drop-shadow-md brightness-[1.8]" loading="lazy" />
                        <span className="text-xs font-semibold" style={{ color: '#00BCD4' }}>PinnacleCart</span>
                      </div>
                    </div>
                  </button>
                );
              }

              // Standard service card
              return (
                <button
                  key={service.id}
                  onClick={() => toggleService(service.id)}
                  className={`group w-full p-4 rounded-2xl border-2 transition-all duration-200 flex items-center gap-4 text-left ${
                    isSelected
                      ? 'border-primary bg-primary/10 shadow-md shadow-primary/10'
                      : 'border-border bg-card hover:border-muted-foreground/30 hover:shadow-sm'
                  }`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                    isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted group-hover:bg-muted/80'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm block">{service.label}</span>
                    <span className="text-xs text-muted-foreground leading-relaxed">{service.description}</span>
                  </div>
                  {isSelected && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isAutoSelected && (
                        <span className="text-[10px] font-medium text-primary/70 bg-primary/10 px-1.5 py-0.5 rounded-full">
                          Included
                        </span>
                      )}
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Currency Selection */}
          {hasBusinessAccount && (
            <div className="animate-fade-in-up">
              <div className="rounded-2xl border-2 border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-semibold text-sm">Currency Accounts</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      EUR is included. Add up to 2 more currencies.
                    </p>
                  </div>
                  {additionalCurrencies.length > 0 && (
                    <Badge variant="default" className="rounded-full">
                      +{additionalCurrencies.length} added
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                  {CURRENCIES.map((currency) => {
                    const isSelected = selectedCurrencies.includes(currency.code);
                    const isEUR = currency.code === 'EUR';
                    const canSelect = isEUR || isSelected || additionalCurrencies.length < 2;

                    return (
                      <button
                        key={currency.code}
                        onClick={() => toggleCurrency(currency.code)}
                        disabled={isEUR || (!isSelected && !canSelect)}
                        className={`p-2.5 rounded-xl border-2 transition-all text-center ${
                          isSelected
                            ? isEUR
                              ? 'border-primary/50 bg-primary/20 cursor-default'
                              : 'border-primary bg-primary/10 shadow-sm'
                            : canSelect
                              ? 'border-border hover:border-muted-foreground/30 hover:shadow-sm'
                              : 'border-border opacity-40 cursor-not-allowed'
                        }`}
                      >
                        <CurrencyFlag currency={currency.code} size="sm" />
                        <span className="text-xs font-semibold block mt-0.5">{currency.code}</span>
                        {isEUR && (
                          <span className="text-[10px] text-muted-foreground">Default</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {additionalCurrencies.length > 0 && (
                  <div className="mt-4 p-3 bg-primary/10 rounded-xl border border-primary/20">
                    <p className="text-xs text-primary text-center font-medium">
                      ✓ EUR + {additionalCurrencies.join(' + ')} accounts will be opened on approval
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-lg border-t border-border safe-area-inset-bottom">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={handleContinue}
            disabled={!isValid}
            className="w-full h-14 text-base font-semibold rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
